import React, { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import styled from "styled-components";
import SettingsSlider from "../../Shared/SettingsSlider";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { styled as MuiStyled } from "@mui/material/styles";
import { Box } from "@mui/material";
import TextView from "../../../TextView";
import * as Styled from "./styles.js";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import IyugaTooltip from "../../../IyugaTooltip";
import { getDesignTabTarget } from "../../../../redux/selectors/segmentBuilderSelectors.js";
import { useSelector } from "react-redux";

const CardContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid ${(props) => (props.isSelected ? "#AA7CFB" : "#e0e0e0")};
  padding: 12px 8px;
  margin-bottom: 8px;
  border-radius: 12px;
  background-color: #f6f7f9;
`;

const DragIcon = MuiStyled(DragIndicatorIcon)`
  font-weight: 600;
  font-size: 25px;
  cursor: grab;  
  color: #bbbbbb;
`;

export default function SegmentReisizingCard({
  segment,
  index,
  moveCard,
  onValueChange,
  toggleValue,
  onLockToggle,
  allSegments,
  onDataValueChange,
  isAutoResizeEnabled = false,
}) {
  const cardRef = useRef(null); // container ref for drop & preview
  const dragRef = useRef(null); // icon ref for drag handle

  const unlockedSegments = allSegments.filter((s) => !s.resize_lock);

  const designTabTarget = useSelector(getDesignTabTarget);

  const [{ handlerId }, drop] = useDrop({
    accept: "CARD",
    collect: (monitor) => ({
      handlerId: monitor.getHandlerId(),
    }),
    hover(item, monitor) {
      if (!cardRef.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = cardRef.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (
        (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) ||
        (dragIndex > hoverIndex && hoverClientY > hoverMiddleY)
      ) {
        return;
      }
      moveCard(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: "CARD",
    item: () => ({ id: segment.id, index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Only apply drop to the entire card container
  drop(cardRef);

  // Make the icon the drag handle
  drag(dragRef);

  // This ensures the entire card is shown during drag, even if grabbed from the icon
  preview(cardRef);

  const handleSliderChange = (newVal) => {
    onValueChange(segment.id, newVal);
  };

  const handleDataInputChange = (e) => {
    const typedStr = e.target.value.trim();
    if (!typedStr) {
      // You could allow empty => 0 or revert
      return;
    }
    let typedNum = parseFloat(typedStr);
    if (isNaN(typedNum) || typedNum < 1) {
      typedNum = 1; // enforce min=1
    }

    onDataValueChange(segment.id, typedNum);
  };

  const isSegmentTooSmall = segment.value <= 1.5;

  return (
    <CardContainer
      ref={cardRef}
      data-handler-id={handlerId}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      isSelected={designTabTarget === "segments" && segment.selected}
    >
      {/* Only dragging the icon triggers the move, but the whole card is the preview */}
      <div ref={dragRef}>
        <DragIcon />
      </div>

      <Box
        sx={{
          width: "100%",
          pointerEvents: isAutoResizeEnabled ? "none" : "auto",
          opacity: isAutoResizeEnabled ? 0.4 : 1,
        }}
      >
        {toggleValue === "percentage" ? (
          <SettingsSlider
            title={segment.settings.title}
            sliderValue={segment.value}
            onValueChange={handleSliderChange}
            min={1}
            max={100}
            valueLabel="%"
            containerStyle={{
              width: "100%",
              margin: "0px",
              gap: "10px",
            }}
            fullSlider
            step={0.5}
            locked={segment.resize_lock}
            onLockToggle={() => onLockToggle(segment.id)}
            disabled={segment.resize_lock || unlockedSegments.length === 1}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <TextView variant="subtitle">{segment.settings.title}</TextView>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isSegmentTooSmall && (
                <IyugaTooltip
                  tooltip="This Segment is smaller than the minimum display size, so it appears as 1.5% of the wheel."
                  placement="top"
                >
                  <InfoOutlinedIcon
                    sx={{
                      color: "#BBBBBB",
                      cursor: "pointer",
                      width: "20px",
                      height: "20px",
                    }}
                  />
                </IyugaTooltip>
              )}
              <Styled.StyledInputResizer
                value={segment.data_value ?? 1}
                disableUnderline
                placeholder=""
                onChange={handleDataInputChange}
              />
            </Box>
          </Box>
        )}
      </Box>
    </CardContainer>
  );
}
